/**
 * FontSizeToggle — accessibility control. Cycles through 6 text-size presets.
 * Toggles a `text-size-*` class on <html>, which overrides `--font-size`.
 * Every text length is rem-based, so they all scale together.
 * Persists to localStorage and is visible to all users.
 */

import { useState, useEffect } from 'react';
import { Type } from 'lucide-react';

const STORAGE_KEY = 'apex_font_size';

type FontSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl' | 'xxl';

const SIZES: FontSize[] = ['xs', 'sm', 'default', 'lg', 'xl', 'xxl'];

const LABELS: Record<FontSize, string> = {
  xs: 'XS',
  sm: 'S',
  default: 'M',
  lg: 'L',
  xl: 'XL',
  xxl: 'XXL',
};

const NON_DEFAULT_CLASSES = [
  'text-size-xs',
  'text-size-sm',
  'text-size-lg',
  'text-size-xl',
  'text-size-xxl',
];

const isFontSize = (v: string | null): v is FontSize =>
  v === 'xs' || v === 'sm' || v === 'default' || v === 'lg' || v === 'xl' || v === 'xxl';

function applyFontSize(size: FontSize) {
  const html = document.documentElement;
  html.classList.remove(...NON_DEFAULT_CLASSES);
  if (size !== 'default') {
    html.classList.add(`text-size-${size}`);
  }
}

export function FontSizeToggle() {
  const [size, setSize] = useState<FontSize>('default');

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const initial: FontSize = isFontSize(stored) ? stored : 'default';
    setSize(initial);
    applyFontSize(initial);
  }, []);

  const cycle = () => {
    const nextIdx = (SIZES.indexOf(size) + 1) % SIZES.length;
    const next = SIZES[nextIdx]!;
    setSize(next);
    applyFontSize(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  return (
    <button
      onClick={cycle}
      aria-label={`Text size: ${LABELS[size]}. Click to change.`}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[0.6875rem] font-medium border transition-colors cursor-pointer bg-secondary/30 border-border/60 text-muted-foreground hover:text-foreground"
      title={`Text size: ${LABELS[size]} (click to change)`}
    >
      <Type className="w-3 h-3 stroke-[1.5]" />
      {LABELS[size]}
    </button>
  );
}
